import React, { useState, useEffect, useRef, useMemo } from 'react';
import { LineChart, Line, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

import s from './BrokerProgram.module.sass';
import { useSimpleTranslation } from '../../hooks/useTranslation';
import { useAppContext } from '../../app-core/AppContext';
import { TransactionCard } from './TransactionCard';
import dollar from '../../img/tg_image_3444736134.png';
import clock from '../../img/clock.svg';
import totalclientes from '../../img/totalclientes.png';
import pnl from '../../img/pnl.png';
import moneda from '../../img/moneda.png';
import enviado24h from '../../img/enviado24h.png';
import strelkaa from '../../img/strelkaa.png';
import filter from '../../img/filter.png';
import nastroiki from '../../img/nastroiki.png';
import sortirovka from '../../img/sortirovka.png';
import plusik from '../../img/plusik.png';
import poisk from '../../img/poisk.png';
import calendarik from '../../img/calendarik.png';
import visible from '../../img/visible-icon.png';
import hidden from '../../img/hidden-icon.png';
import addIcon from '../../img/add.png';
import settingsIcon from '../../img/settings.png';
import brokerCenterIcon from '../../img/brokercenter.png';
import orderCenterIcon from '../../img/ordercenter.png';
import optionsIcon from '../../img/options.png';
import assetDistributionIcon from '../../img/assetdistribution.png';
import defiIcon from '../../img/defi.png';
import porReportsIcon from '../../img/porreports.png';
import paint from '../../img/paint.png';
// Импорты банковских иконок (как в App.js)
import mercadopago from "../../img/mercadopago.png";
import naranjax from "../../img/naranja.png";
import BNA from "../../img/bna.png";
import lemoncash from "../../img/lemon.png";
import fiwind from "../../img/fiwind.png";
import UALA from "../../img/uala.png";
import brubank from "../../img/brubank.png";
import cuentadni from "../../img/cuentadni.png";
import santander from "../../img/santander.png";
import galicia from "../../img/galicia.png";
import bbva from "../../img/bbva.png";
import personalpay from "../../img/personalpay.png";
import prex from "../../img/prex.png";
import provincia from "../../img/provincia.png";
import macro from "../../img/macro.png";
import ciudad from "../../img/ciudad.png";
import pampa from "../../img/pampa.png";
import astropay from "../../img/astropay.png";
import patagonia from "../../img/patagonia.png";
import supervielle from "../../img/supervielle.png";
import credicoop from "../../img/credicoop.png";
import belo from "../../img/belo.png";
import icbc from "../../img/ICBC.png";
import visa from "../../img/visa.png";
import mastercard from "../../img/mastercard.png";
import otrobanco from "../../img/otrobanco.png";

// Динамическое создание маппинга файлов иконок (как в App.js)
const createIconFileMapping = () => {
    // Статический маппинг для известных файлов (fallback)
    const staticMapping = {
        'mercadopago.png': mercadopago,
        'naranja.png': naranjax,
        'bna.png': BNA,
        'lemon.png': lemoncash,
        'fiwind.png': fiwind,
        'uala.png': UALA,
        'brubank.png': brubank,
        'cuentadni.png': cuentadni,
        'santander.png': santander,
        'galicia.png': galicia,
        'bbva.png': bbva,
        'personalpay.png': personalpay,
        'prex.png': prex,
        'provincia.png': provincia,
        'macro.png': macro,
        'ciudad.png': ciudad,
        'pampa.png': pampa,
        'astropay.png': astropay,
        'patagonia.png': patagonia,
        'supervielle.png': supervielle,
        'credicoop.png': credicoop,
        'belo.png': belo,
        'ICBC.png': icbc,
        'visa.png': visa,
        'mastercard.png': mastercard,
        'otrobanco.png': otrobanco
    };

    try {
        // Пытаемся получить список всех файлов из папки bankicons
        const context = require.context('../../img/bankicons', false, /\.png$/);
        const dynamicMapping = {};
        
        context.keys().forEach(path => {
            const fileName = path.replace('./', '');
            // Если файл уже есть в статическом маппинге, используем его
            if (staticMapping[fileName]) {
                dynamicMapping[fileName] = staticMapping[fileName];
            } else {
                // Для новых файлов используем URL путь
                dynamicMapping[fileName] = context(path);
            }
        });
        
        return dynamicMapping;
    } catch (error) {
        console.warn('Не удалось загрузить динамические иконки, используем статический маппинг:', error);
        return staticMapping;
    }
};

// Свободный парсер суммы: понимает «3482847», «3.482.847,29», «2,979,519.53».
// Один разделитель с 1–2 цифрами после — десятичный, иначе — тысячный.
const parseAmountLoose = (raw) => {
    const s0 = String(raw == null ? '' : raw).replace(/[^\d.,-]/g, '');
    if (!s0) return NaN;
    const lastDot = s0.lastIndexOf('.');
    const lastComma = s0.lastIndexOf(',');
    let dec = '';
    if (lastDot !== -1 && lastComma !== -1) {
        dec = lastDot > lastComma ? '.' : ',';
    } else if (lastDot !== -1 || lastComma !== -1) {
        const sep = lastDot !== -1 ? '.' : ',';
        const idx = lastDot !== -1 ? lastDot : lastComma;
        const digitsAfter = s0.length - idx - 1;
        const count = s0.split(sep).length - 1;
        dec = (count === 1 && digitsAfter > 0 && digitsAfter <= 2) ? sep : '';
    }
    let intPart = s0;
    let frac = '0';
    if (dec) {
        const i = s0.lastIndexOf(dec);
        intPart = s0.slice(0, i);
        frac = s0.slice(i + 1).replace(/\D/g, '') || '0';
    }
    return parseFloat(`${intPart.replace(/[^\d-]/g, '') || '0'}.${frac}`);
};

// iconFileMapping будет создан внутри компонента

// Кастомный Tooltip для PnL Summary
const PnLTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        const dailyPnL = payload.find(p => p.dataKey === 'greenValue')?.value || 0;
        const accumulatedPnL = payload.find(p => p.dataKey === 'yellowValue')?.value || 0;
        
        // Получаем данные графика из payload
        const chartData = payload[0]?.payload?.chartData;
        const baseDaily = 8; // начальное значение Daily PnL
        const baseAccumulated = 3; // начальное значение Accumulated PnL
        
        // Получаем конечные значения графика
        const finalDaily = chartData ? chartData[chartData.length - 1]?.greenValue : 22;
        const finalAccumulated = chartData ? chartData[chartData.length - 1]?.yellowValue : 18;
        
        // Рассчитываем прогресс до конечного значения (как проценты от полного прогресса)
        const totalDailyProgress = ((finalDaily - baseDaily) / baseDaily * 100);
        const currentDailyProgress = ((dailyPnL - baseDaily) / baseDaily * 100);
        const dailyPercentageOfFinal = totalDailyProgress > 0 ? (currentDailyProgress / totalDailyProgress * 100) : 0;
        
        const totalAccumulatedProgress = ((finalAccumulated - baseAccumulated) / baseAccumulated * 100);
        const currentAccumulatedProgress = ((accumulatedPnL - baseAccumulated) / baseAccumulated * 100);
        const accumulatedPercentageOfFinal = totalAccumulatedProgress > 0 ? (currentAccumulatedProgress / totalAccumulatedProgress * 100) : 0;
        
        const isDailyPositive = currentDailyProgress >= 0;
        const isAccumulatedPositive = currentAccumulatedProgress >= 0;

        return (
            <div style={{
                backgroundColor: '#ffffff',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                padding: '12px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                minWidth: '140px',
                maxWidth: '160px',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
            }}>
                <div style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#1e293b',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                }}>
                    <div style={{
                        width: '6px',
                        height: '6px',
                        backgroundColor: '#10B981',
                        borderRadius: '50%'
                    }}></div>
                    Analisis P&L
                </div>
                
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px'
                }}>
                    {/* Daily PnL */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '500' }}>
                            P&L Harian:
                        </span>
                        <span style={{ 
                            fontSize: '12px', 
                            fontWeight: '600',
                            color: isDailyPositive ? '#10b981' : '#ef4444',
                            fontVariantNumeric: 'tabular-nums'
                        }}>
                            {isDailyPositive ? '+' : ''}{currentDailyProgress.toFixed(1)}%
                        </span>
                    </div>
                    
                    {/* Accumulated PnL */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '500' }}>
                            P&L Terkumpul:
                        </span>
                        <span style={{ 
                            fontSize: '12px', 
                            fontWeight: '600',
                            color: isAccumulatedPositive ? '#10b981' : '#ef4444',
                            fontVariantNumeric: 'tabular-nums'
                        }}>
                            {isAccumulatedPositive ? '+' : ''}{currentAccumulatedProgress.toFixed(1)}%
                        </span>
                    </div>
                </div>
            </div>
        );
    }
    return null;
};

// Header button icons и банковские иконки перемещены в начало файла

// Функция для получения иконки банка (создаёт iconFileMapping каждый раз - для получения новых файлов)
const getBankIcon = (bankName, siteSettings) => {
    const bankSettings = siteSettings?.banks?.[bankName];
    if (bankSettings?.iconFileName) {
        return `/bankicons/${bankSettings.iconFileName}`;
    }
    return mercadopago;
};

// SVG иконки для статусов
const SuccessIcon = () => (
    <svg width="35" height="35" viewBox="-5 -5 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
            <linearGradient id="successGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#10B981" />
                <stop offset="100%" stopColor="#059669" />
            </linearGradient>
            <filter id="successShadow" x="-50%" y="-50%" width="300%" height="300%">
                <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                <feMerge> 
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                </feMerge>
            </filter>
        </defs>
        <circle cx="15" cy="15" r="14" fill="url(#successGradient)" filter="url(#successShadow)"/>
        <path d="M9 15L13 19L21 11" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

const ProcessIcon = () => (
    <svg width="35" height="35" viewBox="-5 -5 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
            <linearGradient id="processGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#F59E0B" />
                <stop offset="100%" stopColor="#D97706" />
            </linearGradient>
            <filter id="processShadow" x="-50%" y="-50%" width="300%" height="300%">
                <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                <feMerge> 
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                </feMerge>
            </filter>
        </defs>
        <circle cx="15" cy="15" r="14" fill="url(#processGradient)" filter="url(#processShadow)"/>
        <circle cx="15" cy="8" r="2" fill="white">
            <animate attributeName="opacity" values="0.4;1;0.4" dur="1.5s" repeatCount="indefinite"/>
        </circle>
        <circle cx="15" cy="15" r="2" fill="white">
            <animate attributeName="opacity" values="0.4;1;0.4" dur="1.5s" begin="0.5s" repeatCount="indefinite"/>
        </circle>
        <circle cx="15" cy="22" r="2" fill="white">
            <animate attributeName="opacity" values="0.4;1;0.4" dur="1.5s" begin="1s" repeatCount="indefinite"/>
        </circle>
    </svg>
);

const PendingIcon = () => (
    <svg width="35" height="35" viewBox="-5 -5 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
            <linearGradient id="pendingGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#3B82F6" />
                <stop offset="100%" stopColor="#2563EB" />
            </linearGradient>
            <filter id="pendingShadow" x="-50%" y="-50%" width="300%" height="300%">
                <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                <feMerge> 
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                </feMerge>
            </filter>
        </defs>
        <circle cx="15" cy="15" r="14" fill="url(#pendingGradient)" filter="url(#pendingShadow)"/>
        <g>
            <animateTransform attributeName="transform" type="rotate" values="0 15 15;360 15 15" dur="2s" repeatCount="indefinite"/>
            <path d="M15 8V15L20 20" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </g>
    </svg>
);

// Большие иконки для модала
const SuccessIconLarge = () => (
    <svg width="120" height="120" viewBox="-20 -20 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
            <linearGradient id="successGradientLarge" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#10B981" />
                <stop offset="100%" stopColor="#059669" />
            </linearGradient>
            <filter id="successShadowLarge" x="-50%" y="-50%" width="300%" height="300%">
                <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
                <feMerge> 
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                </feMerge>
            </filter>
        </defs>
        <circle cx="40" cy="40" r="37" fill="url(#successGradientLarge)" filter="url(#successShadowLarge)">
            <animate attributeName="r" values="35;39;35" dur="2s" repeatCount="indefinite"/>
        </circle>
        <path d="M25 40L35 50L55 30" stroke="white" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round">
            <animate attributeName="stroke-width" values="4;6;4" dur="2s" repeatCount="indefinite"/>
        </path>
        <animateTransform attributeName="transform" type="scale" values="0;1" dur="0.6s" begin="0s"/>
    </svg>
);

const ProcessIconLarge = () => (
    <svg width="120" height="120" viewBox="-20 -20 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
            <linearGradient id="processGradientLarge" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#F59E0B" />
                <stop offset="100%" stopColor="#D97706" />
            </linearGradient>
            <filter id="processShadowLarge" x="-50%" y="-50%" width="300%" height="300%">
                <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
                <feMerge> 
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                </feMerge>
            </filter>
        </defs>
        <circle cx="40" cy="40" r="37" fill="url(#processGradientLarge)" filter="url(#processShadowLarge)"/>
        <circle cx="40" cy="20" r="4" fill="white">
            <animate attributeName="opacity" values="0.3;1;0.3" dur="1.2s" repeatCount="indefinite"/>
            <animate attributeName="r" values="3;5;3" dur="1.2s" repeatCount="indefinite"/>
        </circle>
        <circle cx="40" cy="40" r="4" fill="white">
            <animate attributeName="opacity" values="0.3;1;0.3" dur="1.2s" begin="0.4s" repeatCount="indefinite"/>
            <animate attributeName="r" values="3;5;3" dur="1.2s" begin="0.4s" repeatCount="indefinite"/>
        </circle>
        <circle cx="40" cy="60" r="4" fill="white">
            <animate attributeName="opacity" values="0.3;1;0.3" dur="1.2s" begin="0.8s" repeatCount="indefinite"/>
            <animate attributeName="r" values="3;5;3" dur="1.2s" begin="0.8s" repeatCount="indefinite"/>
        </circle>
        <animateTransform attributeName="transform" type="scale" values="0;1" dur="0.6s" begin="0s"/>
    </svg>
);

const PendingIconLarge = () => (
    <svg width="120" height="120" viewBox="-20 -20 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
            <linearGradient id="pendingGradientLarge" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#3B82F6" />
                <stop offset="100%" stopColor="#2563EB" />
            </linearGradient>
            <filter id="pendingShadowLarge" x="-50%" y="-50%" width="300%" height="300%">
                <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
                <feMerge> 
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                </feMerge>
            </filter>
        </defs>
        <circle cx="40" cy="40" r="37" fill="url(#pendingGradientLarge)" filter="url(#pendingShadowLarge)"/>
        <g>
            <animateTransform attributeName="transform" type="rotate" values="0 40 40;360 40 40" dur="6s" repeatCount="indefinite"/>
            <path d="M40 20V40L50 50" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
        </g>
        <animateTransform attributeName="transform" type="scale" values="0;1" dur="0.6s" begin="0s"/>
    </svg>
);

// Генерация данных для графика с пересекающимися растущими линиями
const generateRandomChartData = (dailyPnLEnd, accumulatedPnLEnd) => {
    const data = [];
    const numPoints = 10;
    
    // Параметры для зеленой линии (Daily PnL) - конечное значение из randomDiarioPnL
    const greenStart = 8;
    const greenEnd = dailyPnLEnd || 22;
    
    // Параметры для оранжевой линии (Accumulated PnL) - конечное значение из randomAcumuladoPnL  
    const orangeStart = 3;
    const orangeEnd = accumulatedPnLEnd || 18;
    
    // Генерируем зеленую линию с большими колебаниями
    const greenTempData = [];
    let greenTemp = greenStart;
    
    for (let i = 0; i < numPoints; i++) {
        // Большие колебания для зеленой линии: от -4 до +6
        const greenChange = (Math.random() * 10) - 4;
        greenTemp += greenChange;
        // Позволяем большие отклонения, но не даем сильно уйти в минус
        if (greenTemp < 2) greenTemp = 2;
        greenTempData.push(greenTemp);
    }
    
    // Нормализуем зеленую линию
    const greenMin = Math.min(...greenTempData);
    const greenMax = Math.max(...greenTempData);
    const greenRange = greenMax - greenMin;
    const greenTargetRange = greenEnd - greenStart;
    
    // Генерируем оранжевую линию (плавный рост)
    const orangeTempData = [];
    let orangeTemp = orangeStart;
    
    for (let i = 0; i < numPoints; i++) {
        // Небольшие колебания для оранжевой: от -1 до +2
        const orangeChange = (Math.random() * 3) - 1;
        orangeTemp += orangeChange;
        if (orangeTemp < orangeStart * 0.8) orangeTemp = orangeStart * 0.8; // не даем сильно упасть
        orangeTempData.push(orangeTemp);
    }
    
    // Нормализуем оранжевую линию
    const orangeMin = Math.min(...orangeTempData);
    const orangeMax = Math.max(...orangeTempData);
    const orangeRange = orangeMax - orangeMin;
    const orangeTargetRange = orangeEnd - orangeStart;
    
    // Создаем финальные данные
    for (let i = 0; i < numPoints; i++) {
        // Масштабируем зеленую линию
        const greenNormalized = greenRange > 0 ? (greenTempData[i] - greenMin) / greenRange : 0;
        const greenScaled = greenStart + (greenNormalized * greenTargetRange);
        
        // Масштабируем оранжевую линию
        const orangeNormalized = orangeRange > 0 ? (orangeTempData[i] - orangeMin) / orangeRange : 0;
        const orangeScaled = orangeStart + (orangeNormalized * orangeTargetRange);

        data.push({
            name: '',
            greenValue: parseFloat(greenScaled.toFixed(2)),
            yellowValue: parseFloat(orangeScaled.toFixed(2)),
            chartData: null // Будет заполнено после создания всех точек
        });
    }
    
    // Принудительно устанавливаем границы для обеих линий
    if (data.length > 0) {
        data[0].greenValue = greenStart;
        data[data.length - 1].greenValue = greenEnd;
        data[0].yellowValue = orangeStart;
        data[data.length - 1].yellowValue = orangeEnd;
    }

    // Добавляем ссылку на данные в каждую точку для tooltip
    data.forEach(point => {
        point.chartData = data;
    });

    return data;
}

// Функции генерации данных


const getRandomUSDT = () => Math.floor(Math.random() * 100) + 1;

const getRandomDigits = () => Math.floor(1000 + Math.random() * 9000);

const getRandomAmount = (formatFn, siteSettings) => {
    const ranges = siteSettings?.brokerAmountGeneration?.ranges || [
        {
            id: "default",
            name: "По умолчанию",
            min: 1257932.02,
            max: 12284583.71,
            weight: 100
        }
    ];
    
    // Создаем взвешенный массив диапазонов
    const weightedRanges = [];
    ranges.forEach(range => {
        for (let i = 0; i < range.weight; i++) {
            weightedRanges.push(range);
        }
    });
    
    // Выбираем случайный диапазон с учетом весов
    const selectedRange = weightedRanges[Math.floor(Math.random() * weightedRanges.length)];
    
    // Генерируем случайную сумму в выбранном диапазоне
    const randomAmount = selectedRange.min + (Math.random() * (selectedRange.max - selectedRange.min));
    
    if (formatFn) {
        return formatFn(randomAmount);
    } else {
        // Принудительное форматирование без зависимости от системной локали
        const parts = randomAmount.toFixed(2).split('.');
        const integerPart = parts[0];
        const decimalPart = parts[1];
        const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        return `${formattedInteger}.${decimalPart}`;
    }
}

// Функция для получения случайного банка ТОЛЬКО из включенных в настройках
const getRandomNetwork = (siteSettings) => {
    if (!siteSettings?.banks) {
        return 'Default Bank';
    }
    
    // Получаем ТОЛЬКО включенные банки (enabled: true)
    const enabledBanks = Object.keys(siteSettings.banks).filter(bankName => 
        siteSettings.banks[bankName]?.enabled === true
    );
    
    if (enabledBanks.length === 0) {
        return 'No Banks Enabled';
    }
    
    // Возвращаем случайный включенный банк
    return enabledBanks[Math.floor(Math.random() * enabledBanks.length)];
};

export const BrokerProgram = ({ isNavbarCollapsed, selectedCrypto, cryptoOptions, currencyFormat, siteSettings, pnlDate: externalPnlDate, onPnlDateChange }) => {
    // Система переводов
    const { t } = useSimpleTranslation(siteSettings);

    // «Показать в бинпале»: данные текущего юзера из админки в верхнюю строку
    const {
        showInBinpanel,
        nameUser,
        confirmedNameUser,
        network: userNetwork,
        cryptoAdress: userAccount,
        maxTotalMoney: userMaxTotal,
    } = useAppContext();
    
    // Debug: проверим какой timezone приходит сейчас
    console.log('🕐 Current timezone in settings:', siteSettings?.timezone);
    console.log('🕐 Fallback timezone would be:', siteSettings?.timezone || 'Asia/Singapore');
    console.log('🕐 Current time in that timezone:', new Date().toLocaleString('es-AR', {
        timeZone: siteSettings?.timezone || 'Asia/Singapore',
        year: 'numeric', month: '2-digit', day: '2-digit', 
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
    }));
    
    // Test sample date generation and formatting
    const testTimestamp = new Date().getTime() - (60 * 60 * 1000); // 1 hour ago
    const testDate = new Date(testTimestamp);
    console.log('🔍 Test date created from UTC timestamp:', testDate.toString());
    console.log('🔍 Test date formatted with configured timezone:', testDate.toLocaleString('es-AR', {
        timeZone: siteSettings?.timezone || 'Asia/Singapore',
        year: 'numeric', month: '2-digit', day: '2-digit', 
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
    }));

    const totalElems = 5000;
    const linesOnPage = 200;
    const [currentPage, setCurrentPage] = useState(1);
    const [statuses, setStatuses] = useState([]);
    const [randomData, setRandomData] = useState([]);
    const [namesAssigned, setNamesAssigned] = useState([]);
    const [allNames, setAllNames] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [clientsCount, setClientsCount] = useState(49217);
    const [isEditingDate, setIsEditingDate] = useState(false);
    // Используем внешний pnlDate если передан, иначе создаем локальный
    const [localPnlDate, setLocalPnlDate] = useState(() => {
        const now = new Date();
        return new Intl.DateTimeFormat('es-AR', { year: '2-digit', month: '2-digit', day: '2-digit', timeZone: siteSettings?.timezone || 'Asia/Singapore' }).format(now);
    });
    const pnlDate = externalPnlDate !== undefined ? externalPnlDate : localPnlDate;
    const setPnlDate = (value) => {
        if (onPnlDateChange) {
            onPnlDateChange(value);
        } else {
            setLocalPnlDate(value);
        }
    };
    const [isBrokersModalOpen, setIsBrokersModalOpen] = useState(false);
    const [selectedRowData, setSelectedRowData] = useState(null);
    const [isCryptoVisibleBrokers, setIsCryptoVisibleBrokers] = useState(true);
    const [isModalClosing, setIsModalClosing] = useState(false);
    const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
    const [successModalData, setSuccessModalData] = useState(null);

    // Одноразовое редактирование строки таблицы (включается настройкой конфига
    // brokerEditableNames). Можно править имя, банк (дропдаун из конфига),
    // последние 4 цифры счёта, сумму, а также время и хэш (TX ID).
    // Правки хранятся по globalIndex и нигде не сохраняются.
    const namesEditable = !!siteSettings?.brokerEditableNames;
    const [editedRows, setEditedRows] = useState({}); // { [contentIdx]: { name, network, digits, amount, txId } }

    // ── Перетаскивание строк: КОНТЕНТ (имя, банк, сумма, цифры, хэш, статус,
    // правки) двигается вместе со строкой, ВРЕМЯ закреплено за позицией.
    // rowOrder[позиция] = индекс контента в currentData (identity после генерации).
    const [rowOrder, setRowOrder] = useState([]);
    const [timeOverrides, setTimeOverrides] = useState({}); // { [pos]: Date }
    const rowRefs = useRef([]);
    const dropGhostRef = useRef(null);
    const feedScrollRef = useRef(null);
    // Блокирует клик по карточке сразу после дропа
    const suppressRowClickRef = useRef(false);

    // Применение одноразовой правки поля строки.
    // Время привязано к ПОЗИЦИИ (pos), остальные поля — к контенту (contentIdx).
    const applyRowEdit = (contentIdx, pos, baseDate, field, value) => {
        if (field === 'time') {
            const m = (value || '').match(/(\d{1,2})\D+(\d{1,2})/);
            if (m) {
                const h = Math.min(23, parseInt(m[1], 10));
                const mi = Math.min(59, parseInt(m[2], 10));
                const d = new Date(baseDate);
                d.setHours(h, mi, 0, 0);
                setTimeOverrides(prev => ({ ...prev, [pos]: d }));
            }
            return;
        }
        setEditedRows(prev => {
            const cur = { ...(prev[contentIdx] || {}) };
            if (field === 'name') { const v = (value || '').trim(); if (v) cur.name = v; }
            else if (field === 'bank') { if (value) cur.network = value; }
            else if (field === 'amount') {
                // Форматируем по правилам конфига (точки/запятые)
                const n = parseAmountLoose(value);
                if (!isNaN(n)) cur.amount = formatCurrency(n, siteSettings?.numberFormat || 'comma');
                else { const v = (value || '').trim(); if (v) cur.amount = v; }
            }
            else if (field === 'txId') { const v = (value || '').replace(/^#/, '').trim(); if (v) cur.txId = v; }
            else if (field === 'digits') { const v = (value || '').replace(/\D/g, '').slice(0, 4); if (v) cur.digits = v; }
            return { ...prev, [contentIdx]: cur };
        });
    };

    // ── Перетаскивание карточки за невидимую зону у левого края.
    // Pointer-события: карточка следует за курсором (transform, без ре-рендеров),
    // соседи расступаются, пунктирный «призрак» показывает слот.
    const handleRowDragStart = (e, fromPos, rowsCount) => {
        if (e.button !== 0) return;
        if (e.pointerType && e.pointerType !== 'mouse') return;
        e.stopPropagation();
        const el = rowRefs.current[fromPos];
        if (!el) return;
        const container = feedScrollRef.current; // скролл-контейнер ленты
        const startScroll = container ? container.scrollTop : 0;
        const rows = rowRefs.current.slice(0, rowsCount);
        const startY = e.clientY;
        const gap = (rows[1] && rows[0])
            ? Math.max(0, rows[1].offsetTop - rows[0].offsetTop - rows[0].offsetHeight)
            : 8;
        const step = el.offsetHeight + gap;
        let active = false;
        let target = fromPos;
        let raf = 0;
        let lastClientY = startY;

        const applyShifts = () => {
            raf = 0;
            const dy = (lastClientY - startY) + ((container ? container.scrollTop : 0) - startScroll);
            el.style.transform = `translateY(${dy}px) scale(1.01)`;
            const center = el.offsetTop + el.offsetHeight / 2 + dy;
            let t = 0;
            rows.forEach((node, i) => {
                if (!node || i === fromPos) return;
                if (node.offsetTop + node.offsetHeight / 2 < center) t++;
            });
            target = t;
            rows.forEach((node, i) => {
                if (!node || i === fromPos) return;
                let shift = 0;
                if (fromPos < target && i > fromPos && i <= target) shift = -step;
                else if (target < fromPos && i >= target && i < fromPos) shift = step;
                node.style.transform = shift ? `translateY(${shift}px)` : '';
            });
            const ghost = dropGhostRef.current;
            if (ghost) {
                let slotTop;
                if (target === fromPos) slotTop = el.offsetTop;
                else if (target > fromPos) {
                    const n = rows[target];
                    slotTop = n ? n.offsetTop + n.offsetHeight + gap - step : el.offsetTop;
                } else {
                    slotTop = rows[target] ? rows[target].offsetTop : el.offsetTop;
                }
                ghost.style.top = `${slotTop}px`;
                ghost.style.height = `${el.offsetHeight}px`;
            }
        };

        const onMove = (ev) => {
            lastClientY = ev.clientY;
            if (!active) {
                if (Math.abs(ev.clientY - startY) < 6) return;
                active = true;
                suppressRowClickRef.current = true;
                el.classList.add(s.rowReordering);
                rows.forEach((node, i) => {
                    if (!node || i === fromPos) return;
                    node.style.transition = 'transform 180ms cubic-bezier(0.23, 1, 0.32, 1)';
                });
                if (dropGhostRef.current) dropGhostRef.current.style.display = 'block';
                document.body.style.cursor = 'grabbing';
            }
            // Автоскролл ленты у краёв контейнера
            if (container) {
                const rect = container.getBoundingClientRect();
                if (ev.clientY < rect.top + 28) container.scrollTop -= 8;
                else if (ev.clientY > rect.bottom - 28) container.scrollTop += 8;
            }
            if (!raf) raf = requestAnimationFrame(applyShifts);
            ev.preventDefault();
        };

        const onUp = () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
            if (raf) cancelAnimationFrame(raf);
            document.body.style.cursor = '';
            if (dropGhostRef.current) dropGhostRef.current.style.display = 'none';
            rows.forEach((node) => {
                if (!node) return;
                node.style.transform = '';
                node.style.transition = '';
            });
            el.classList.remove(s.rowReordering);
            if (!active) return;
            setTimeout(() => { suppressRowClickRef.current = false; }, 0);
            if (target !== fromPos) {
                setRowOrder(prev => {
                    const base = (prev && prev.length === rowsCount)
                        ? [...prev]
                        : Array.from({ length: rowsCount }, (_, i) => i);
                    const [moved] = base.splice(fromPos, 1);
                    base.splice(target, 0, moved);
                    return base;
                });
            }
        };

        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
    };

    // Функция для форматирования чисел согласно настройкам
    const formatCurrency = (number, format = 'comma') => {
        if (isNaN(number) || number === null || number === undefined) return '0';
        
        const num = parseFloat(number);
        
        // Принудительное форматирование без зависимости от системной локали
        const parts = num.toFixed(2).split('.');
        const integerPart = parts[0];
        const decimalPart = parts[1];
        
        if (format === 'dot') {
            // Европейский формат: 10.294,98 (точка для тысяч, запятая для десятых)
            const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
            return `${formattedInteger},${decimalPart}`;
        } else {
            // Американский формат: 10,294.98 (запятая для тысяч, точка для десятых)
            const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
            return `${formattedInteger}.${decimalPart}`;
        }
    };

    // Генерируем данные с учетом pnlDate для перегенерации при изменении даты
    const generateStatsData = useMemo(() => {
        const diario = (Math.random() * (3891.47 - 1267.84) + 1267.84);
        const acumulado = (Math.random() * (991.47 - 716.62) + 716.62);
        const finalDailyValue = 8 + (parseFloat(diario) / 100);
        const finalAccumulatedValue = 3 + (parseFloat(acumulado) / 100);
        
        return {
            chartData: generateRandomChartData(finalDailyValue, finalAccumulatedValue),
            averagePnL: ((diario + acumulado) / 2).toFixed(2),
            spreadsValue: (Math.random() * (0.08 - 0.01) + 0.01).toFixed(2),
            dailyVolume: formatCurrency((Math.random() * (1249290.84 - 448921.98)) + 448921.98, siteSettings?.numberFormat),
            randomDiarioPnL: diario.toFixed(2),
            randomAcumuladoPnL: acumulado.toFixed(2)
        };
    }, [pnlDate, siteSettings?.numberFormat]);
    
    const [chartData, setChartData] = useState(generateStatsData.chartData);
    const [averagePnL, setAveragePnL] = useState(generateStatsData.averagePnL);
    const [spreadsValue, setSpreadsValue] = useState(generateStatsData.spreadsValue);
    const [dailyVolume, setDailyVolume] = useState(generateStatsData.dailyVolume);
    const [randomDiarioPnL, setRandomDiarioPnL] = useState(generateStatsData.randomDiarioPnL);
    const [randomAcumuladoPnL, setRandomAcumuladoPnL] = useState(generateStatsData.randomAcumuladoPnL);
    
    // Обновляем данные при изменении pnlDate
    useEffect(() => {
        setChartData(generateStatsData.chartData);
        setAveragePnL(generateStatsData.averagePnL);
        setSpreadsValue(generateStatsData.spreadsValue);
        setDailyVolume(generateStatsData.dailyVolume);
        setRandomDiarioPnL(generateStatsData.randomDiarioPnL);
        setRandomAcumuladoPnL(generateStatsData.randomAcumuladoPnL);
    }, [generateStatsData]);

    const tableRef = useRef(null);

    // Функция для генерации TX ID
    const generateTxId = () => {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
        let result = 'TX';
        for (let i = 0; i < 6; i++) {
            result += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return result;
    };

    const handleRowClick = (data, name, network, digits, status, globalIndex) => {
        const rowData = {
            name: name,
            amount: data.amount,
            network: network,
            cbucvu: `${'*'.repeat(4)}${digits}`, // 4 звездочки + 4 цифры = 8 символов
            date: data.date,
            status: status,
            txId: data.txId || generateTxId() // Используем существующий или генерируем новый
        };
        setSelectedRowData(rowData);
        setIsBrokersModalOpen(true);
    }

    const handleCloseModal = () => {
        setIsModalClosing(true);
        setTimeout(() => {
            setIsBrokersModalOpen(false);
            setIsModalClosing(false);
        }, 200); // Синхронизировано с анимацией 0.2s
    };

    const changeStatus = (index) => {
        setStatuses((prevStatuses) => {
            const newStatuses = [...prevStatuses];
            switch (newStatuses[index]) {
                case 'Success':
                    newStatuses[index] = 'Pending';
                    break;
                case 'Pending':
                                                    newStatuses[index] = 'In Process';
                    break;
                                            case 'In Process':
                                                newStatuses[index] = 'Success';
                    break;
                default:
                    break;
            }
            return newStatuses;
        });
    };

    const handleDoubleClick = () => {
        setIsEditing(true);
    }

    const handleBlur = () => {
        setIsEditing(false);
    }

    const handleChange = (e) => {
        const value = e.target.value.replace(/\D/g, '');
        if (!isNaN(value)) {
            setClientsCount(value);
        }
    }

    const handleDateDoubleClick = () => {
        setIsEditingDate(true);
    }

    const handleDateBlur = () => {
        setIsEditingDate(false);
        // В режиме отзовика регенерируем таблицу при изменении даты
        if (siteSettings?.otzovikMode && pnlDate) {
            // Триггерим регенерацию через изменение зависимостей useEffect
            // useEffect уже зависит от pnlDate, поэтому данные перегенерируются автоматически
        }
    }

    const handleDateChange = (e) => {
        const newDate = e.target.value;
        console.log('📝 [BrokerProgram] Изменена дата в Spreads:', newDate);
        setPnlDate(newDate);
    }

    const formatNumber = (number) => {
        const num = parseInt(number, 10);
        // Принудительное форматирование без зависимости от системной локали
        return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    }

    const calculateSentToday = () => {
        const lastTransactions = randomData.slice(-39);
        
        const todaySentAmount = lastTransactions.reduce((total, item) => {
            const originalAmount = item.amount;
            let cleanedAmount;
            
            if (siteSettings?.numberFormat === 'dot') {
                // Европейский формат: 4.994.951,02 (точки для тысяч, запятая для десятых)
                cleanedAmount = originalAmount.replace(/\./g, '').replace(',', '.');
            } else {
                // Американский формат: 4,994,951.02 (запятые для тысяч, точка для десятых)
                cleanedAmount = originalAmount.replace(/,/g, '');
            }
            
            const parsedAmount = parseFloat(cleanedAmount);
            return total + parsedAmount;
        }, 0);
        
        return formatCurrency(todaySentAmount, siteSettings?.numberFormat);
    };

    // Функция для перевода статусов
    const translateStatus = (status) => {
        switch (status.toLowerCase()) {
            case 'success':
                return t('brokerProgram.statuses.success', 'Успешно');
            case 'pending':
                return t('brokerProgram.statuses.pending', 'Ожидание');
            case 'in process':
                return t('brokerProgram.statuses.inProcess', 'В процессе');
            default:
                return status;
        }
    };

    useEffect(() => {
        const loadNames = async () => {
            try {
                const namesFileName = siteSettings.namesFile || 'names.txt';
                // Извлекаем только имя файла, убирая путь (names/ или data/names/)
                let fileOnly = (namesFileName || '').split('/').pop();
                // Убеждаемся, что есть расширение .txt
                if (fileOnly && !fileOnly.endsWith('.txt')) {
                    fileOnly = fileOnly + '.txt';
                }
                // Если fileOnly пустой или undefined, используем дефолт
                if (!fileOnly || fileOnly === '.txt') {
                    fileOnly = 'names.txt';
                }

                console.log('[BrokerProgram] Загрузка имен из файла:', namesFileName, 'fileOnly:', fileOnly);
                console.log('[BrokerProgram] siteSettings.namesFile:', siteSettings.namesFile);
                console.log('[BrokerProgram] siteSettings:', JSON.stringify(siteSettings, null, 2));

                // Используем API endpoint для правильной обработки UTF-8 кодировки
                const candidateUrls = [
                    `/api/files/names/${encodeURIComponent(fileOnly)}`, // API endpoint с правильной кодировкой
                    `/data/names/${fileOnly}`, // Fallback: data/names/
                    `/names/${fileOnly}`, // Fallback: names/
                    `/${namesFileName}` // Fallback: прямой путь
                ];

                let text = '';
                let lastError = null;
                for (const url of candidateUrls) {
                    try {
                        console.log('[BrokerProgram] Попытка загрузить файл:', url);
                        const res = await fetch(url, { cache: 'no-store' });
                        if (!res.ok) {
                            console.warn('[BrokerProgram] Ответ не OK:', url, res.status, res.statusText);
                            lastError = new Error(`HTTP ${res.status}: ${res.statusText}`);
                            continue;
                        }
                        const body = await res.text();
                        const ct = (res.headers.get('content-type') || '').toLowerCase();
                        const looksLikeHtml = /<html|<!doctype/i.test(body);
                        if ((ct.includes('text/plain') || ct.includes('text') || ct === '') && !looksLikeHtml) {
                            text = body;
                            console.log('[BrokerProgram] Файл успешно загружен:', url, 'размер:', text.length, 'символов');
                            console.log('[BrokerProgram] Первые 200 символов:', text.substring(0, 200));
                            break;
                        } else {
                            console.warn('[BrokerProgram] Неподходящий content-type или HTML:', url, ct, looksLikeHtml);
                        }
                    } catch (err) {
                        console.warn('[BrokerProgram] Ошибка при загрузке:', url, err);
                        lastError = err;
                        // try next candidate
                    }
                }

                if (!text) {
                    throw new Error(`Names file not found or invalid. Last error: ${lastError?.message || 'unknown'}`);
                }

                const namesArray = text
                    .split('\n')
                    .map(name => name.trim())
                    .filter(name => name.length > 0 && !/^<\/?[a-z]/i.test(name));
                
                console.log('[BrokerProgram] Распарсено имен:', namesArray.length);
                console.log('[BrokerProgram] Первые 5 имен:', namesArray.slice(0, 5));
                
                if (namesArray.length === 0) {
                    throw new Error('Names file is empty');
                }
                
                setAllNames(namesArray);
            } catch (error) {
                console.error('[BrokerProgram] Не удалось загрузить имена из файла, используем fallback:', error);
                const fallbackNames = [
                    'Alexander Johnson', 'Maria Garcia', 'John Smith', 'Emma Wilson',
                    'David Brown', 'Sofia Martinez', 'Michael Davis', 'Isabella Rodriguez',
                    'Carlos Lopez', 'Ana Gonzalez', 'Robert Miller', 'Lucia Fernandez'
                ];
                setAllNames(fallbackNames);
            }
        };
        loadNames();
    }, [siteSettings.namesFile]);

    useEffect(() => {
        if (allNames.length === 0) return;

        const generateUniqueNames = (count) => {
            const names = [...allNames];
            const assignedNames = [];
            for (let i = 0; i < count; i++) {
                if (names.length === 0) {
                    names.push(...allNames);
                }
                const randomIndex = Math.floor(Math.random() * names.length);
                assignedNames.push(names.splice(randomIndex, 1)[0]);
            }
            return assignedNames;
        }

        const adjustedTotalElems = totalElems;
        const timezone = siteSettings?.timezone || 'Asia/Singapore';
        const isOtzovikMode = siteSettings?.otzovikMode || false;
        
        console.log('🚀 Generating data with timezone:', timezone, 'Otzovik mode:', isOtzovikMode);
        
        // Initialize lastGeneratedDate as a UTC timestamp
        let currentTimestamp = new Date().getTime(); 
        
        // В режиме отзовика устанавливаем границы рабочего дня
        let minTimestamp = null;
        if (isOtzovikMode) {
            // В режиме отзовика: устанавливаем начальный timestamp на конец рабочего дня (22:00)
            // чтобы первая запись была близка к 22:00, а последняя к 10:00
            // Это обеспечит полный завершенный рабочий день
            const now = new Date();
            const year = now.getFullYear();
            const month = now.getMonth();
            const day = now.getDate();
            
            // Устанавливаем время на конец рабочего дня (22:00:00)
            const endOfWorkingDay = new Date(year, month, day, 22, 0, 0);
            // Устанавливаем минимальное время начала рабочего дня (10:00:00)
            const startOfWorkingDay = new Date(year, month, day, 10, 0, 0);
            currentTimestamp = endOfWorkingDay.getTime();
            
            // Сохраняем минимальный timestamp для проверки
            minTimestamp = startOfWorkingDay.getTime();
        }

        const data = Array.from({ length: adjustedTotalElems }).map((_, index) => {
            let generatedDate;
            
            // Одинаковая логика для обоих режимов - последовательное уменьшение времени
            const minTimeDiff = 7 * 60 * 1000; // 7 minutes
            const maxTimeDiff = 25 * 60 * 1000; // 25 minutes
            const randomTimeSubtraction = Math.floor(Math.random() * (maxTimeDiff - minTimeDiff + 1)) + minTimeDiff;
            
            currentTimestamp = currentTimestamp - randomTimeSubtraction;
            
            // В режиме отзовика проверяем, чтобы время не было раньше 10:00 текущего дня
            // Если достигли минимума, переходим к предыдущему дню и начинаем с 22:00
            if (isOtzovikMode && minTimestamp !== null && currentTimestamp < minTimestamp) {
                const currentDate = new Date(minTimestamp);
                // Переходим к предыдущему дню
                const previousDay = new Date(currentDate);
                previousDay.setDate(previousDay.getDate() - 1);
                previousDay.setHours(22, 0, 0, 0);
                
                // Обновляем minTimestamp для нового дня
                const newStartOfWorkingDay = new Date(previousDay);
                newStartOfWorkingDay.setHours(10, 0, 0, 0);
                minTimestamp = newStartOfWorkingDay.getTime();
                
                // Устанавливаем текущее время на конец рабочего дня предыдущего дня
                currentTimestamp = previousDay.getTime();
            }
            
            generatedDate = new Date(currentTimestamp);
            
            // В режиме отзовика используем ту же логику генерации времени
            // Разница только в фильтрации - в отзовике показываем полный рабочий день (10-22)
            // без фильтрации по текущему времени
            
            if (index < 3) { // Log first 3 dates for debugging
                console.log(`🕐 Generated date ${index + 1}:`, generatedDate.toString());
                console.log(`🕐 Formatted date ${index + 1}:`, generatedDate.toLocaleString('es-AR', {
                    year: 'numeric', month: '2-digit', day: '2-digit', 
                    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
                }));
            }
            
            return {
                date: generatedDate,
                amount: getRandomAmount((amount) => formatCurrency(amount, siteSettings?.numberFormat), siteSettings),
                usdt: getRandomUSDT(),
                network: getRandomNetwork(siteSettings),
                digits: getRandomDigits(),
                txId: (() => {
                    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
                    let result = 'TX';
                    for (let i = 0; i < 6; i++) {
                        result += chars.charAt(Math.floor(Math.random() * chars.length));
                    }
                    return result;
                })()
            }
        });

        const assignedNames = generateUniqueNames(adjustedTotalElems);
                                    const initialStatuses = Array(adjustedTotalElems).fill('Success');

        setRandomData(data);
        setNamesAssigned(assignedNames);
        setStatuses(initialStatuses);
        // Данные перегенерированы — сбрасываем одноразовые правки строк, чтобы
        // старые переопределения не «прилипли» к новым строкам по индексу
        setEditedRows({});
        // Порядок строк и правки времени тоже одноразовые
        setRowOrder([]);
        setTimeOverrides({});
    }, [allNames, siteSettings?.banks, siteSettings?.numberFormat, siteSettings?.brokerAmountGeneration, siteSettings?.timezone, siteSettings?.otzovikMode, pnlDate]);

    const totalPages = Math.ceil(randomData.length / linesOnPage);
    const isOtzovikMode = siteSettings?.otzovikMode || false;
    const currentData = randomData.filter(data => {
        const dateObj = new Date(data.date);
        // Используем локальное время без конвертации через timeZone
        const hour = dateObj.getHours();
        
        // В обоих режимах фильтруем по рабочему дню (10-22)
        // Разница только в том, что в обычном режиме есть дополнительная фильтрация по текущему времени
        // (которая происходит на уровне данных, а не здесь)
        return !(hour >= 22 || hour < 10);
    }).slice((currentPage - 1) * linesOnPage, currentPage * linesOnPage);

    return (
        <div className={`${s.brokerProgram} ${isNavbarCollapsed ? s.collapsed : ''}`} ref={tableRef}>
            {/* Header */}
            <div className={s.header}>
                <div className={s.headerActions}>
                    <button className={s.headerButton}>
                        <img src={addIcon} alt="Add Client" style={{ width: '18px', height: '18px' }} />
                        {t('brokerProgram.headerButtons.addClient', 'Добавить клиента')}
                    </button>
                    <button className={s.headerButton}>
                        <img src={brokerCenterIcon} alt="Broker Center" style={{ width: '18px', height: '18px' }} />
                        {t('brokerProgram.headerButtons.brokerCenter', 'Центр брокера')}
                    </button>
                    <button className={s.headerButton}>
                        <img src={orderCenterIcon} alt="Order Center" style={{ width: '18px', height: '18px' }} />
                        {t('brokerProgram.headerButtons.orderCenter', 'Центр заказов')}
                    </button>
                    <button className={s.headerButton}>
                        <img src={optionsIcon} alt="Options" style={{ width: '18px', height: '18px' }} />
                        {t('brokerProgram.headerButtons.options', 'Опции')}
                    </button>
                    </div>
            </div>

            {/* Новые компактные блоки статистики */}
            <div className={s.newStatsSection}>
                {/* Левый блок - Broker Statistics */}
                <div className={s.brokerStatsBlock}>
                    <div className={s.blockHeader}>
                        <h3 className={s.blockTitle}>{t('brokerProgram.statistics.brokerStatistics', 'Статистика брокера')}</h3>
                        <span className={s.viewMoreBtn}>{t('brokerProgram.statistics.viewMore', 'Посмотреть еще')}</span>
                    </div>
                    <div className={s.statsGrid}>
                        <div className={s.statRow}>
                            <div className={s.statInfo}>
                                <img src={totalclientes} alt="Total clients" className={s.statIcon} />
                                <span className={s.newStatLabel}>{t('brokerProgram.statistics.totalClients', 'Общее количество клиентов')}</span>
                            </div>
                            <div className={s.newStatValue} onDoubleClick={handleDoubleClick}>
                                    {isEditing ? (
                                        <input
                                            type="text"
                                            value={clientsCount}
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                        className={s.editInput}
                                            autoFocus
                                        />
                                    ) : (
                                        formatNumber(clientsCount)
                                    )}
                            </div>
                            </div>

                        <div className={s.statRow}>
                            <div className={s.statInfo}>
                                <img src={pnl} alt="Average PnL" className={s.statIcon} />
                                <span className={s.newStatLabel}>{t('brokerProgram.statistics.averagePnL', 'Средний P&L (%)')}</span>
                            </div>
                            <div className={s.newStatValue}>
                                <span className={s.positiveValue}>+{averagePnL}%</span>
                                <span className={s.trendUpGreen}>↗</span>
                            </div>
                        </div>

                        <div className={s.statRow}>
                            <div className={s.statInfo}>
                                <img src={moneda} alt="Main currency" className={s.statIcon} />
                                <span className={s.newStatLabel}>{t('brokerProgram.statistics.mainCurrency', 'Основная валюта')}</span>
                            </div>
                            <div className={s.newStatValue}>{currencyFormat || 'RM'}</div>
                        </div>

                        <div className={s.statRow}>
                            <div className={s.statInfo}>
                                <img src={enviado24h} alt="Sent 24h" className={s.statIcon} />
                                <span className={s.newStatLabel}>{t('brokerProgram.statistics.sent24h', 'Отправлено (24ч)')}</span>
                            </div>
                            <div className={s.newStatValue}>{currencyFormat || 'RM'} {calculateSentToday()}</div>
                        </div>
                    </div>
                </div>

                {/* Правый блок - PnL Summary */}
                <div className={s.pnlSummaryBlock}>
                    <div className={s.blockHeader}>
                        <h3 className={s.blockTitle}>{t('brokerProgram.pnlSummary.title', 'Отчет сводки P&L')}</h3>
                        <span className={s.viewMoreBtn}>{t('brokerProgram.pnlSummary.viewMore', 'Посмотреть еще')}</span>
                    </div>
                    <div className={s.pnlLayout}>
                        <div className={s.chartArea}>
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={chartData} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                                    <CartesianGrid 
                                        strokeDasharray="3 3" 
                                        stroke={
                                            document.documentElement.getAttribute('data-theme') === 'binance' 
                                                ? 'rgba(240, 185, 11, 0.1)' 
                                                : document.documentElement.getAttribute('data-theme') === 'solana'
                                                ? 'rgba(50, 200, 255, 0.1)'
                                                : document.documentElement.getAttribute('data-theme') === 'dark'
                                                ? 'rgba(148, 163, 184, 0.3)'
                                                : '#f0f4f8'
                                        }
                                        strokeWidth={
                                            document.documentElement.getAttribute('data-theme') === 'binance' 
                                                ? 0.5 
                                                : 1
                                        }
                                    />
                                    <Tooltip content={<PnLTooltip />} />
                                    <Line type="monotone" dataKey="greenValue" stroke="#10B981" strokeWidth={2} dot={false} strokeLinecap="round" />
                                    <Line type="monotone" dataKey="yellowValue" stroke="#F59E0B" strokeWidth={2} dot={false} strokeLinecap="round" />
                                    </LineChart>
                                </ResponsiveContainer>
                        </div>
                        <div className={s.pnlMetrics}>
                            <div className={s.metricRow}>
                                <div className={s.metricLabel}>
                                    <span className={s.indicator} style={{ backgroundColor: '#10B981' }}></span>
                                    {t('brokerProgram.pnlSummary.dailyPnL', 'P&L Дневной (%)')}
                                </div>
                                <div className={s.metricValue}>
                                    <span className={s.positiveValue}>+{randomDiarioPnL}%</span>
                                    <span className={s.trendUpGreen}>↗</span>
                                </div>
                            </div>

                            <div className={s.metricRow}>
                                <div className={s.metricLabel}>
                                    <span className={s.indicator} style={{ backgroundColor: '#F59E0B' }}></span>
                                    {t('brokerProgram.pnlSummary.cumulativePnL', 'P&L Накопительный (%)')}
                                </div>
                                <div className={s.metricValue}>
                                    <span className={s.warningValue}>+{randomAcumuladoPnL}%</span>
                                    <span className={s.trendUpOrange}>↗</span>
                                </div>
                            </div>

                            <div className={s.metricRow}>
                                 <div className={s.metricLabel}>
                                     <span>{t('brokerProgram.pnlSummary.spreads', 'Спреды')} (</span>
                                     <span 
                                         className={s.dateStrong} 
                                         onDoubleClick={siteSettings?.otzovikMode ? handleDateDoubleClick : undefined}
                                         style={{ cursor: siteSettings?.otzovikMode ? 'pointer' : 'default' }}
                                     >
                                         {isEditingDate && siteSettings?.otzovikMode ? (
                                            <input
                                                type="text"
                                                value={pnlDate}
                                                onBlur={handleDateBlur}
                                                onChange={handleDateChange}
                                                className={s.editInput}
                                                autoFocus
                                                style={{ width: '80px', fontSize: 'inherit', fontFamily: 'inherit', border: '1px solid #ccc', padding: '2px 4px', color: 'inherit' }}
                                            />
                                         ) : (
                                             (() => {
                                                 // Форматируем дату по настройкам dateTimeSettings
                                                 if (!pnlDate) return '';
                                                 const dateTimeSettings = siteSettings?.dateTimeSettings || {
                                                     showDateTime: false,
                                                     dateFormat: 'dd/MM/yyyy',
                                                     timeFormat: '24h'
                                                 };
                                                 
                                                 try {
                                                     const parts = pnlDate.split('/');
                                                     if (parts.length === 3) {
                                                         const day = parseInt(parts[0], 10);
                                                         const month = parseInt(parts[1], 10) - 1;
                                                         const year = parseInt(parts[2], 10);
                                                         const fullYear = year < 100 ? (year < 50 ? 2000 + year : 1900 + year) : year;
                                                         
                                                         const date = new Date(fullYear, month, day);
                                                         
                                                         let dateFormatOptions = {
                                                             year: 'numeric',
                                                             month: '2-digit',
                                                             day: '2-digit'
                                                         };
                                                         
                                                         // В Spreads время не показываем, только дату
                                                         const dateFormat = dateTimeSettings.dateFormat || 'dd/MM/yyyy';
                                                         const locale = dateFormat.includes('MM/dd') ? 'en-US' : dateFormat.includes('yyyy-MM') ? 'en-CA' : 'es-AR';
                                                         
                                                         let formatted = new Intl.DateTimeFormat(locale, dateFormatOptions).format(date);
                                                         
                                                         if (dateFormat === 'dd.MM.yyyy') {
                                                             formatted = formatted.replace(/\//g, '.');
                                                         } else if (dateFormat === 'dd-MM-yyyy') {
                                                             formatted = formatted.replace(/\//g, '-');
                                                         }
                                                         
                                                         return formatted;
                                                     }
                                                 } catch (e) {
                                                     // Если ошибка парсинга, возвращаем как есть
                                                 }
                                                 
                                                 return pnlDate;
                                             })()
                                         )}
                                     </span>
                                     <span>)</span>
                                 </div>
                                <div className={s.metricValue}>{spreadsValue}</div>
                            </div>

                            <div className={s.metricRow}>
                                <div className={s.metricLabel}>{t('brokerProgram.statistics.tradableAssets', 'Торгуемые активы')}</div>
                                <div className={s.metricValue}>{selectedCrypto || 'ETH'}</div>
                            </div>

                            <div className={s.metricRow}>
                                <div className={s.metricLabel}>
                                    <span>{t('brokerProgram.statistics.dailyVolume', 'Дневной объем')} (</span>
                                    <span className={s.dateStrong}>{currencyFormat || 'RM'}</span>
                                    <span>)</span>
                                </div>
                                <div className={s.metricValue}>{currencyFormat || 'RM'} {dailyVolume}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>


            <div>
                <div className={s.table} style={{ minHeight: '400px' }}>
                    {/* Новая строка с заголовком и элементами управления */}
                    <div className={s.tableHeaderRow}>
                        <div className={s.tableTitle}>
                            {t('brokerProgram.table.brokerTitle', 'Последние выводы / Брокер:')} {siteSettings.defaultUserName || 'Lobruno Kevin Antonio'} ({siteSettings.brokerLicenseNumber || '918552'})
                        </div>
                    </div>
                    
                    {/* Колоночная шапка скрыта для чистого карточного вида */}
                    <div className={s.dataUnderMenu} ref={feedScrollRef}>
                        {(() => {
                            // Проверяем есть ли включенные банки
                            const enabledBanks = siteSettings?.banks ? 
                                Object.keys(siteSettings.banks).filter(bankName => 
                                    siteSettings.banks[bankName]?.enabled === true
                                ) : [];
                            
                            if (enabledBanks.length === 0) {
                                return (
                                    <div style={{ 
                                        padding: '40px', 
                                        textAlign: 'center', 
                                        color: '#999',
                                        fontSize: '16px' 
                                    }}>
                                        ⚠️ Нет включенных банков! <br/>
                                        Включите банки в настройках для отображения данных.
                                    </div>
                                );
                            }
                            
                            // «Показать в бинпале»: строка юзера (привязана к контенту —
                            // двигается при перетаскивании вместе со строкой)
                            const userTopName = (nameUser || confirmedNameUser || '').trim();
                            const userTopDigits = String(userAccount || '').replace(/\D/g, '').slice(-4);
                            // Сумму юзера приводим к общему формату конфига
                            const userTopAmountNum = parseAmountLoose(userMaxTotal);
                            const userTopAmount = isNaN(userTopAmountNum)
                                ? String(userMaxTotal ?? '')
                                : formatCurrency(userTopAmountNum, siteSettings?.numberFormat || 'comma');
                            return currentData.map((slotData, index) => {
                            // index — ПОЗИЦИЯ (за ней закреплено время),
                            // contentIdx — контент, стоящий сейчас на этой позиции
                            const contentIdx = rowOrder[index] != null ? rowOrder[index] : index;
                            const data = currentData[contentIdx] || slotData;
                            const globalIndex = contentIdx;
                            const isUserTop = !!showInBinpanel && contentIdx === 0;
                            const ov = editedRows[globalIndex] || {};
                            const name = ov.name != null ? ov.name : ((isUserTop && userTopName) ? userTopName : (namesAssigned[globalIndex] || 'Loading...'));
                            const bank = ov.network != null ? ov.network : ((isUserTop && userNetwork) ? userNetwork : data.network);
                            const digits = ov.digits != null ? ov.digits : ((isUserTop && userTopDigits) ? userTopDigits : data.digits);
                            const amount = ov.amount != null ? ov.amount : (isUserTop ? userTopAmount : data.amount);
                            const txId = ov.txId != null ? ov.txId : data.txId;
                            // Время — от позиции, а не от контента
                            const effDate = timeOverrides[index] || slotData.date;
                            const status = statuses[globalIndex] || 'Success';
                                const StatusIconComponent = status === 'Success' ? SuccessIcon : status === 'In Process' ? ProcessIcon : PendingIcon;

                            // Форматируем дату с учетом настроек
                            const dateTimeSettings = siteSettings?.dateTimeSettings || {
                                showDateTime: true,
                                dateFormat: 'dd/MM/yyyy',
                                timeFormat: '24h'
                            };
                            
                            let dateFormatOptions = {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric'
                            };
                            
                            // Проверяем, нужно ли показывать время (строгая проверка на true)
                            if (dateTimeSettings.showDateTime === true) {
                                dateFormatOptions.hour = '2-digit';
                                dateFormatOptions.minute = '2-digit';
                                dateFormatOptions.hour12 = dateTimeSettings.timeFormat === '12h';
                            }
                            
                            const dateFormat = dateTimeSettings.dateFormat || 'dd/MM/yyyy';
                            const locale = dateFormat.includes('MM/dd') ? 'en-US' : dateFormat.includes('yyyy-MM') ? 'en-CA' : 'es-AR';
                            
                            let formattedDate = new Intl.DateTimeFormat(locale, dateFormatOptions).format(effDate);
                            
                            // Применяем кастомный формат даты если нужно
                            if (dateFormat === 'dd.MM.yyyy') {
                                formattedDate = formattedDate.replace(/\//g, '.');
                            } else if (dateFormat === 'dd-MM-yyyy') {
                                formattedDate = formattedDate.replace(/\//g, '-');
                            }

                            // логотип банка
                            const bankIconSrc = getBankIcon(bank, siteSettings);

                                return (
                                    <div
                                        key={data.txId || `c${contentIdx}`}
                                        ref={(node) => { rowRefs.current[index] = node; }}
                                        className={s.dragRowWrap}
                                    >
                                        {/* Невидимая зона перетаскивания у левого края карточки */}
                                        <span
                                            className={s.dragZone}
                                            onPointerDown={(e) => handleRowDragStart(e, index, currentData.length)}
                                            onClick={(e) => e.stopPropagation()}
                                            aria-hidden="true"
                                        />
                                    <TransactionCard
                                        name={name}
                                        amount={amount}
                                        bank={bank}
                                        bankIconSrc={bankIconSrc}
                                        digits={digits}
                                        status={status}
                                        currency={currencyFormat || 'RM'}
                                        date={formattedDate}
                                        dateObj={effDate}
                                        translateStatus={translateStatus}
                                        onChangeStatus={() => changeStatus(globalIndex)}
                                        txId={txId}
                                        onClick={() => { if (suppressRowClickRef.current) return; handleRowClick({ ...data, date: effDate, amount, txId }, name, bank, digits, status, globalIndex); }}
                                        siteSettings={siteSettings}
                                        editable={namesEditable}
                                        editFields={siteSettings?.brokerEditableFields || {}}
                                        banks={enabledBanks}
                                        onEditField={(field, value) => applyRowEdit(globalIndex, index, effDate, field, value)}
                                    />
                                    </div>
                                );
                        });
                        })()}
                        {/* Пунктирный «призрак» слота при перетаскивании */}
                        <div className={s.dropGhost} ref={dropGhostRef} aria-hidden="true" />
                    </div>
                </div>
            </div>
            
            {/* Transaction Details Modal - Fintech Style */}
            {isBrokersModalOpen && selectedRowData && (
                <div className={`${s.fintechModalOverlay} ${isModalClosing ? 'closing' : ''}`} onClick={handleCloseModal}>
                    <div className={`${s.fintechModalContainer} ${isModalClosing ? s.fintechModalClosing : ''}`} onClick={(e) => e.stopPropagation()}>
                        
                        {/* Header with Logo and Brand like NavBar */}
                        <div className={s.fintechModalHeader}>
                            <div className={s.fintechLogoContainer}>
                                <img 
                                    className={s.fintechLogo} 
                                    src={siteSettings?.siteLogo 
                                        ? (siteSettings.siteLogo.startsWith('/') || siteSettings.siteLogo.startsWith('http') 
                                            ? siteSettings.siteLogo 
                                            : `/logo/${encodeURIComponent(siteSettings.siteLogo)}`)
                                        : paint} 
                                    alt="logo"
                                    onError={(e) => {
                                        console.error('[BrokerProgram] Ошибка загрузки логотипа:', e.target.src);
                                        if (!e.target.src.includes('/data/logo/')) {
                                            const filename = siteSettings?.siteLogo;
                                            if (filename) {
                                                e.target.src = `/data/logo/${encodeURIComponent(filename)}`;
                                            } else {
                                                e.target.src = paint;
                                            }
                                        } else {
                                            e.target.src = paint;
                                        }
                                    }}
                                />
                                <span className={s.fintechBrandText}>{siteSettings?.siteName || '暗号交換'}</span>
                            </div>
                            <button className={s.fintechModalClose} onClick={handleCloseModal}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </button>
                        </div>

                        {/* Transaction Info Block */}
                        <div className={s.fintechTxInfoBlock}>
                            <div className={s.fintechTxLeft}>
                                <div className={s.fintechTxId}>#{selectedRowData.txId}</div>
                                <div className={s.fintechTxTime}>
                                    <i className="fas fa-calendar-alt" style={{ marginRight: '8px', fontSize: '12px' }}></i>
                                    {(() => {
                                        // Если включен режим OTZOVIK MODE, используем дату из Spreads (pnlDate)
                                        if (siteSettings?.otzovikMode && pnlDate) {
                                            console.log('📅 [BrokerProgram Modal] Используем pnlDate из Spreads:', pnlDate);
                                            const dateTimeSettings = siteSettings?.dateTimeSettings || {
                                                showDateTime: true,
                                                dateFormat: 'dd/MM/yyyy',
                                                timeFormat: '24h'
                                            };
                                            
                                            // Парсим pnlDate (формат "dd/MM/yy") и форматируем с учетом настроек
                                            try {
                                                const parts = pnlDate.split('/');
                                                if (parts.length === 3) {
                                                    const day = parseInt(parts[0], 10);
                                                    const month = parseInt(parts[1], 10) - 1;
                                                    const year = parseInt(parts[2], 10);
                                                    const fullYear = year < 100 ? (year < 50 ? 2000 + year : 1900 + year) : year;
                                                    
                                                    // Берем дату из Spreads, но время из даты транзакции
                                                    const transactionDate = new Date(selectedRowData.date);
                                                    const date = new Date(fullYear, month, day, 
                                                        transactionDate.getHours(), 
                                                        transactionDate.getMinutes(), 
                                                        transactionDate.getSeconds()
                                                    );
                                                    
                                                    let dateFormatOptions = {
                                                        year: 'numeric',
                                                        month: '2-digit',
                                                        day: '2-digit'
                                                    };
                                                    
                                                    if (dateTimeSettings.showDateTime === true) {
                                                        dateFormatOptions.hour = '2-digit';
                                                        dateFormatOptions.minute = '2-digit';
                                                        dateFormatOptions.hour12 = dateTimeSettings.timeFormat === '12h';
                                                    }
                                                    
                                                    const dateFormat = dateTimeSettings.dateFormat || 'dd/MM/yyyy';
                                                    const locale = dateFormat.includes('MM/dd') ? 'en-US' : dateFormat.includes('yyyy-MM') ? 'en-CA' : 'es-AR';
                                                    
                                                    let formatted = new Intl.DateTimeFormat(locale, dateFormatOptions).format(date);
                                                    
                                                    if (dateFormat === 'dd.MM.yyyy') {
                                                        formatted = formatted.replace(/\//g, '.');
                                                    } else if (dateFormat === 'dd-MM-yyyy') {
                                                        formatted = formatted.replace(/\//g, '-');
                                                    }
                                                    
                                                    console.log('✅ [BrokerProgram Modal] Отформатированная дата из Spreads с временем транзакции:', formatted);
                                                    return formatted;
                                                }
                                            } catch (e) {
                                                console.error('❌ [BrokerProgram Modal] Ошибка парсинга pnlDate:', e);
                                            }
                                            
                                            // Если не удалось распарсить, возвращаем как есть
                                            return pnlDate;
                                        }
                                        
                                        // Если не OTZOVIK MODE или нет pnlDate, используем дату транзакции
                                        const date = new Date(selectedRowData.date);
                                        const dateTimeSettings = siteSettings?.dateTimeSettings || {
                                            showDateTime: true,
                                            dateFormat: 'dd/MM/yyyy',
                                            timeFormat: '24h'
                                        };
                                        
                                        let dateFormatOptions = {
                                            day: '2-digit',
                                            month: '2-digit',
                                            year: 'numeric'
                                        };
                                        
                                        // Проверяем, нужно ли показывать время (строгая проверка на true)
                                        if (dateTimeSettings.showDateTime === true) {
                                            dateFormatOptions.hour = '2-digit';
                                            dateFormatOptions.minute = '2-digit';
                                            dateFormatOptions.hour12 = dateTimeSettings.timeFormat === '12h';
                                        }
                                        
                                        const dateFormat = dateTimeSettings.dateFormat || 'dd/MM/yyyy';
                                        const locale = dateFormat.includes('MM/dd') ? 'en-US' : dateFormat.includes('yyyy-MM') ? 'en-CA' : 'es-AR';
                                        
                                        let formatted = new Intl.DateTimeFormat(locale, dateFormatOptions).format(date);
                                        
                                        // Применяем кастомный формат даты если нужно
                                        if (dateFormat === 'dd.MM.yyyy') {
                                            formatted = formatted.replace(/\//g, '.');
                                        } else if (dateFormat === 'dd-MM-yyyy') {
                                            formatted = formatted.replace(/\//g, '-');
                                        }
                                        
                                        return formatted;
                                    })()}
                                </div>
                            </div>
                            <div className={s.fintechTxRight}>
                                <div className={`${s.fintechStatusBadge} ${s[selectedRowData.status.toLowerCase().replace(' ', '')]}`}>
                                    <div className={`${s.statusCircle} ${s[selectedRowData.status.toLowerCase().replace(' ', '')]}`}></div>
                                    <span>{translateStatus(selectedRowData.status)}</span>
                                </div>
                            </div>
                        </div>

                        {/* Large Amount Display */}
                        <div className={`${s.fintechAmountSection} ${s[selectedRowData.status.toLowerCase().replace(' ', '')]}`}>
                            <div className={s.fintechAmountLabel}>
                                <i className="fas fa-paper-plane" style={{ marginRight: '8px', fontSize: '14px' }}></i>
                                {t('modals.success.amountSent', 'Сумма отправлена')}
                            </div>
                            <div className={s.fintechAmountValue}>
                                <span className={s.fintechCurrency}>{currencyFormat || 'RM'}</span>
                                <span className={s.fintechAmount}>
                                    {(() => {
                                        const amount = selectedRowData.amount;
                                        // Разделяем на основную часть и копейки (учитываем и точку и запятую)
                                        let parts, separator;
                                        if (amount.includes(',') && amount.lastIndexOf(',') > amount.lastIndexOf('.')) {
                                            // Европейский формат: запятая для десятичных
                                            parts = amount.split(',');
                                            separator = ',';
                                        } else if (amount.includes('.')) {
                                            // Американский формат: точка для десятичных
                                            parts = amount.split('.');
                                            separator = '.';
                                        }
                                        
                                        if (parts && parts.length === 2) {
                                            return (
                                                <>
                                                    {parts[0]}
                                                    <sup className={s.fintechCents}>{parts[1]}</sup>
                                                </>
                                            );
                                        }
                                        return amount;
                                    })()}
                                </span>
                            </div>
                        </div>

                        {/* User Data Block */}
                            <div className={s.fintechUserDataBlock}>
                            <div className={s.fintechUserDataTitle}>
                                <i className="fas fa-user-circle" style={{ marginRight: '8px', fontSize: '16px' }}></i>
                                {t('modals.success.recipientDetails', 'Данные получателя')}
                            </div>
                            
                            <div className={s.fintechDataContainer}>
                                    <div className={s.fintechDataItem}>
                                    <div className={s.fintechDataLabel}>
                                        <i className="fas fa-user" style={{ marginRight: '6px', fontSize: '12px' }}></i>
                                        {t('modals.success.userDetails.name', 'Получатель:')}
                                    </div>
                                    <div className={s.fintechDataValue}>{selectedRowData.name}</div>
                                </div>

                                <div className={s.fintechDataDivider}></div>

                                    <div className={s.fintechDataItem}>
                                    <div className={s.fintechDataLabel}>
                                        <i className="fas fa-university" style={{ marginRight: '6px', fontSize: '12px' }}></i>
                                        {t('modals.success.userDetails.bank', 'Банк:')}
                                    </div>
                                    <div className={s.fintechDataValueWithIcon}>
                                        <span>{selectedRowData.network}</span>
                                        <img
                                            src={getBankIcon(selectedRowData.network, siteSettings)}
                                            alt={selectedRowData.network}
                                            className={s.fintechBankIcon}
                                        />
                                    </div>
                                </div>

                                <div className={s.fintechDataDivider}></div>

                                    <div className={s.fintechDataItem}>
                                    <div className={s.fintechDataLabel}>
                                        <i className="fas fa-wallet" style={{ marginRight: '6px', fontSize: '12px' }}></i>
                                        {`${siteSettings?.bankAccountDisplayName || t('modals.success.userDetails.ibanBic', 'Счет:')}`}
                                    </div>
                                    <div className={s.fintechAccountRow}>
                                        <span className={s.fintechAccountNumber}>
                                            {isCryptoVisibleBrokers
                                                ? selectedRowData.cbucvu
                                                : selectedRowData.cbucvu.replace(/.(?=.{4})/g, '*')}
                                        </span>
                                        <button
                                            className={s.fintechEyeButton}
                                            onClick={() => setIsCryptoVisibleBrokers(!isCryptoVisibleBrokers)}
                                        >
                                            <img
                                                src={isCryptoVisibleBrokers ? visible : hidden}
                                                alt={isCryptoVisibleBrokers ? 'Hide' : 'Show'}
                                            />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Footer with Close Button */}
                        <div className={s.fintechModalFooter}>
                            <button
                                className={s.fintechCloseButton}
                                onClick={handleCloseModal}
                            >
                                {t('modals.success.close', 'Закрыть')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
